/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
/* eslint-disable react/no-multi-comp */
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { alpha, Box, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { ChartsTooltipContainer, useItemTooltip } from '@mui/x-charts/ChartsTooltip';
import { useDrawingArea } from '@mui/x-charts/hooks';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import {
  formatBRL,
  getCategoriaChartColor,
  resolveTipoNotaDef,
} from './constants';
import {
  CHART_HEIGHT,
  LocalFilterChip,
  toggleLocalCategoria,
} from './chartShared';

const PIE_MARGIN = { left: 8, right: 120, top: 8, bottom: 8 };

const PieEmpty = ({ message }) => (
  <Box
    sx={{
      height: CHART_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      gap: 1,
    }}
  >
    <PieChartOutlineIcon sx={{ fontSize: 40, opacity: 0.4 }} />
    <Typography variant="body2">{message}</Typography>
  </Box>
);

PieEmpty.propTypes = {
  message: PropTypes.string.isRequired,
};

const PieCenterLabel = ({ title, value }) => {
  const theme = useTheme();
  const { width, height, left, top } = useDrawingArea();
  const cx = left + width / 2;
  const cy = top + height / 2;
  const valueText = String(value ?? '');
  const valueFontSize = valueText.length > 16 ? 11 : valueText.length > 12 ? 12 : 14;

  return (
    <g pointerEvents="none" aria-hidden="true">
      <text
        x={cx}
        y={cy - 10}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={theme.palette.text.secondary}
        style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4 }}
      >
        {title}
      </text>
      <text
        x={cx}
        y={cy + 12}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={theme.palette.text.primary}
        style={{ fontSize: valueFontSize, fontWeight: 700 }}
      >
        {value}
      </text>
    </g>
  );
};

PieCenterLabel.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const formatPercent = (value) =>
  value != null
    ? `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    : null;

const DistribuicaoTooltipContent = ({ pieData }) => {
  const item = useItemTooltip();
  if (!item) return null;

  const dataIndex = item.identifier?.dataIndex;
  const row = dataIndex != null ? pieData[dataIndex] : null;
  const categoria = row?.categoria
    || (typeof item.label === 'string' ? item.label : null)
    || '—';
  const rawValue = typeof row?.value === 'number'
    ? row.value
    : (typeof item.value === 'number' ? item.value : null);
  const valor = formatBRL(rawValue);
  const percentual = formatPercent(row?.percentual);

  return (
    <ChartsTooltipContainer trigger="item">
      <Paper elevation={4} sx={{ px: 1.5, py: 1, minWidth: 160, borderRadius: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {categoria}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {valor}
          </Typography>
          {percentual && (
            <Typography variant="caption" color="text.secondary">
              ({percentual})
            </Typography>
          )}
        </Stack>
      </Paper>
    </ChartsTooltipContainer>
  );
};

DistribuicaoTooltipContent.propTypes = {
  pieData: PropTypes.array.isRequired,
};

const DistribuicaoChart = ({ items, loading, totalGasto, height }) => {
  const chartHeight = height || CHART_HEIGHT;
  const [localCategoria, setLocalCategoria] = useState(null);

  const pieData = useMemo(
    () =>
      (items || [])
        .filter((item) => Number(item.valor) > 0)
        .map((item, index) => {
          const valor = Number(item.valor) || 0;
          const def = resolveTipoNotaDef(item.tipoNota, item.categoria);
          const categoria = def.label;
          const percentual = item.percentual != null ? Number(item.percentual) : null;
          const brl = formatBRL(valor);
          const pct = formatPercent(percentual);
          const baseColor = getCategoriaChartColor(item.categoria, item.tipoNota);
          const isDimmed = localCategoria && localCategoria !== categoria;
          return {
            id: def.enum || `cat-${index}`,
            value: valor,
            categoria,
            tipoNota: def.enum,
            label: (location) => {
              if (location === 'legend') {
                return pct ? `${categoria} — ${brl} (${pct})` : `${categoria} — ${brl}`;
              }
              return categoria;
            },
            percentual,
            color: isDimmed ? alpha(baseColor, 0.22) : baseColor,
          };
        }),
    [items, localCategoria],
  );

  const pieColors = pieData.map((item) => item.color);
  const hasData = pieData.length > 0;

  const filteredTotal = useMemo(() => {
    if (!localCategoria) return totalGasto;
    const row = pieData.find((item) => item.categoria === localCategoria);
    return Number(row?.value) || 0;
  }, [localCategoria, pieData, totalGasto]);

  const TooltipSlot = useMemo(
    () => function DistribuicaoTooltipSlot() {
      return <DistribuicaoTooltipContent pieData={pieData} />;
    },
    [pieData],
  );

  const series = useMemo(
    () => [
      {
        data: pieData,
        innerRadius: 50,
        paddingAngle: 2,
        cornerRadius: 4,
        highlightScope: { fade: 'global', highlight: 'item' },
      },
    ],
    [pieData],
  );

  const handleSelectCategoria = useCallback((categoria) => {
    setLocalCategoria((prev) => toggleLocalCategoria(prev, categoria));
  }, []);

  const handleItemClick = useCallback(
    (_event, identifier) => {
      const row = pieData[identifier?.dataIndex];
      if (row?.categoria) handleSelectCategoria(row.categoria);
    },
    [pieData, handleSelectCategoria],
  );

  const handleLegendClick = useCallback(
    (_event, legendItem) => {
      const dataIndex = legendItem?.dataIndex;
      if (dataIndex == null) return;
      const row = pieData[dataIndex];
      if (row?.categoria) handleSelectCategoria(row.categoria);
    },
    [pieData, handleSelectCategoria],
  );

  if (loading) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  if (!hasData) {
    return <PieEmpty message="Sem gastos no período selecionado" />;
  }

  return (
    <Box sx={{ width: '100%' }} aria-label="Gráfico de distribuição de gastos por categoria">
      <LocalFilterChip
        label={localCategoria ? `Categoria: ${localCategoria}` : null}
        onClear={() => setLocalCategoria(null)}
      />
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', overflow: 'visible' }}>
        <PieChart
          series={series}
          colors={pieColors}
          height={chartHeight}
          margin={PIE_MARGIN}
          slots={{ tooltip: TooltipSlot }}
          onItemClick={handleItemClick}
          slotProps={{
            legend: {
              position: { vertical: 'middle', horizontal: 'right' },
              onItemClick: handleLegendClick,
            },
          }}
          sx={{
            overflow: 'visible',
            cursor: 'pointer',
            '& .MuiChartsLegend-root button': { cursor: 'pointer' },
          }}
        >
          {filteredTotal > 0 && (
            <PieCenterLabel
              title={localCategoria ? localCategoria : 'Total'}
              value={formatBRL(filteredTotal)}
            />
          )}
        </PieChart>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Clique em uma fatia ou legenda para destacar a categoria neste gráfico
      </Typography>
    </Box>
  );
};

DistribuicaoChart.propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  totalGasto: PropTypes.number,
  height: PropTypes.number,
};

DistribuicaoChart.defaultProps = {
  items: [],
  loading: false,
  totalGasto: 0,
  height: null,
};

export default DistribuicaoChart;
